import type { TFunction } from "i18next";

export type InteractionMode =
  | "explain"
  | "evidence"
  | "policy"
  | "remediation"
  | "confidence"
  | "lineage"
  | "freeform_scoped";

export type InteractionCitation = {
  entityType?: string;
  entityId?: string;
  chunkId?: string;
  documentId?: string;
  filename?: string;
  pageNumber?: string | number | null;
  sectionTitle?: string | null;
  excerpt?: string | null;
  label?: string | null;
  confidence?: number;
  freshness?: number;
  stale?: boolean;
};

export type InteractionResult = {
  answer?: string | null;
  confidence?: string | null;
  insufficientContext: boolean;
  citations: InteractionCitation[];
  lineageSummary?: Array<Record<string, unknown>>;
  retrievalMode?: string | null;
  providerScope?: string | null;
  reasoningRunId?: string | null;
  ragRunId?: string | null;
  mode?: string | null;
  scopeBadge?: { intelligenceId: string; type: string; title: string };
  answeredAt?: string;
};

export type LoadingPhase = "retrieving" | "validating" | "policy";

const EXCERPT_MAX = 220;

export function normalizeConfidenceLevel(value: string | null | undefined): string {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"].includes(raw)) return raw;
  if (raw.includes("INSUFFICIENT")) return "INSUFFICIENT";
  if (raw.includes("HIGH")) return "HIGH";
  if (raw.includes("LOW")) return "LOW";
  return "MEDIUM";
}

export function confidenceBadgeClass(level: string): string {
  switch (level) {
    case "HIGH":
      return "border-emerald-200 bg-emerald-50 text-emerald-900";
    case "MEDIUM":
      return "border-blue-200 bg-blue-50 text-blue-900";
    case "LOW":
      return "border-amber-200 bg-amber-50 text-amber-900";
    case "INSUFFICIENT":
    default:
      return "border-amber-300 bg-amber-50 text-amber-900";
  }
}

export function citationDedupKey(c: InteractionCitation): string {
  const file = c.filename || c.label || c.documentId || "";
  const section = c.sectionTitle || "";
  const page = c.pageNumber != null ? String(c.pageNumber) : "";
  const entity = `${c.entityType || ""}:${c.entityId || ""}`;
  return `${file}|${section}|${page}|${entity}`.toLowerCase();
}

export function dedupeCitations(citations: InteractionCitation[]): InteractionCitation[] {
  const seen = new Map<string, InteractionCitation>();
  for (const c of citations) {
    const key = citationDedupKey(c);
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, c);
      continue;
    }
    const mergedConfidence = Math.max(existing.confidence ?? 0, c.confidence ?? 0);
    seen.set(key, { ...existing, confidence: mergedConfidence });
  }
  return [...seen.values()];
}

export function formatCitationTitle(c: InteractionCitation): string {
  if (c.filename) return c.filename;
  if (c.label) return c.label;
  if (c.entityType?.includes("policy")) return "Policy reference";
  if (c.entityType?.includes("evidence")) return "Evidence reference";
  return "Governance reference";
}

export function truncateExcerpt(excerpt: string | null | undefined): string {
  if (!excerpt) return "";
  const text = String(excerpt).trim();
  if (text.length <= EXCERPT_MAX) return text;
  return `${text.slice(0, EXCERPT_MAX)}…`;
}

export function isCitationStale(c: InteractionCitation): boolean {
  if (c.stale) return true;
  if (c.freshness != null && c.freshness < 0.35) return true;
  return false;
}

export function mapRetrievalModeLabel(mode: string | null | undefined, t: TFunction): string {
  const key = String(mode || "INTELLIGENCE_CONTEXT").toUpperCase();
  return t(`complianceCenterUi.govIntelligenceInteraction.retrieval.${key}`, {
    defaultValue: t("complianceCenterUi.govIntelligenceInteraction.retrieval.INTELLIGENCE_CONTEXT", "Governance context"),
  });
}

export function mapProviderScopeLabel(scope: string | null | undefined, t: TFunction): string {
  const key = String(scope || "DETERMINISTIC").toUpperCase();
  return t(`complianceCenterUi.govIntelligenceInteraction.retrieval.${key}`, {
    defaultValue: scope || "—",
  });
}

export function mapInteractionError(message: string, status?: number, t?: TFunction): string {
  const msg = String(message || "").toLowerCase();
  const tr = (key: string, fallback: string) =>
    t ? t(`complianceCenterUi.govIntelligenceInteraction.error.${key}`, fallback) : fallback;

  if (status === 403) return tr("forbidden", "You do not have permission for this action.");
  if (status === 404) return tr("notFound", "Governance intelligence item not found.");
  if (msg.includes("timeout") || msg.includes("timed out")) return tr("timeout", "Retrieval timed out. Try again.");
  if (msg.includes("local") && msg.includes("provider")) return tr("localProviderMissing", "Local AI provider is not configured.");
  if (msg.includes("provider") && (msg.includes("unavailable") || msg.includes("disabled"))) {
    return tr("providerUnavailable", "AI provider is temporarily unavailable.");
  }
  if (msg.includes("confidential") || msg.includes("blocked") || msg.includes("policy")) {
    return tr("confidentialityBlock", "Confidentiality policy blocked this request.");
  }
  if (msg.includes("scope") || msg.includes("scoped")) return tr("scopeRequired", "Keep your question scoped to the selected item.");
  return tr("generic", "Unable to complete this request. Please try again.");
}

export const CHIP_MODES: InteractionMode[] = [
  "explain",
  "evidence",
  "policy",
  "remediation",
  "confidence",
  "lineage",
];

export function chipQuestionKey(mode: InteractionMode): string {
  return `complianceCenterUi.govIntelligenceInteraction.chips.${mode}.question`;
}

export function chipLabelKey(mode: InteractionMode): string {
  return `complianceCenterUi.govIntelligenceInteraction.chips.${mode}.label`;
}
